import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CVExperienceManagerProps, ExperienceEntry, ProjectEntry } from './types';
import { validateRequired } from './utils/validation';
import Input from '../UI/Input';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import { Edit, Plus, Save, Trash } from "lucide-react";

/**
 * CV Experience Manager component
 * Handles CRUD operations for both experience and project entries
 * Allows overlapping date ranges without validation errors
 */
const CVExperienceManager: React.FC<CVExperienceManagerProps> = ({
  experiences,
  projects,
  onAddExperience,
  onAddProject,
  onUpdateExperience,
  onUpdateProject,
  onDeleteExperience,
  onDeleteProject
}) => {
  const [showModal, setShowModal] = useState<Record<'experience' | 'projects', boolean>>({
    experience: false,
    projects: false
  });
  const [editingId, setEditingId] = useState<Record<'experience' | 'projects', number | null>>({
    experience: null,
    projects: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form for adding/editing experience entries
  const experienceForm = useForm<ExperienceEntry>({
    mode: 'onBlur',
    defaultValues: {
      organization_name: '',
      position: '',
      start_month: 1,
      start_year: new Date().getFullYear(),
      end_month: undefined,
      end_year: undefined,
      responsibilities: '',
      achievements: ''
    }
  });

  // Form for adding/editing project entries
  const projectForm = useForm<ProjectEntry>({
    mode: 'onBlur',
    defaultValues: {
      project_name: '',
      role: '',
      start_month: 1,
      start_year: new Date().getFullYear(),
      end_month: undefined,
      end_year: undefined,
      description: '',
      results: ''
    }
  });

  // Month options for dropdowns
  const monthOptions = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' }
  ];

  // Validate experience entry data
  const validateExperienceEntry = (data: ExperienceEntry): { isValid: boolean; errors: Record<string, string[]> } => {
    const validationErrors: Record<string, string[]> = {};

    // Required field validation
    validationErrors.organization_name = validateRequired(data.organization_name, 'Organization name');
    validationErrors.position = validateRequired(data.position, 'Position');
    validationErrors.responsibilities = validateRequired(data.responsibilities, 'Responsibilities');

    // Year validation (basic checks, no overlap validation per requirements)
    if (!data.start_year) {
      validationErrors.start_year = ['Start year is required'];
    } else if (data.start_year > new Date().getFullYear()) {
      validationErrors.start_year = ['Start year cannot be in the future'];
    }

    // Month validation
    if (!data.start_month || data.start_month < 1 || data.start_month > 12) {
      validationErrors.start_month = ['Start month is required'];
    }

    // End date validation (optional, but if provided should be valid)
    if (data.end_year && data.end_year > new Date().getFullYear()) {
      validationErrors.end_year = ['End year cannot be in the future'];
    }

    if (data.end_month && (data.end_month < 1 || data.end_month > 12)) {
      validationErrors.end_month = ['End month must be between 1 and 12'];
    }

    // Remove empty error arrays
    Object.keys(validationErrors).forEach(key => {
      if (validationErrors[key].length === 0) {
        delete validationErrors[key];
      }
    });

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  };

  // Validate project entry data
  const validateProjectEntry = (data: ProjectEntry): { isValid: boolean; errors: Record<string, string[]> } => {
    const validationErrors: Record<string, string[]> = {};

    // Required field validation
    validationErrors.project_name = validateRequired(data.project_name, 'Project name');
    validationErrors.role = validateRequired(data.role, 'Role');
    validationErrors.description = validateRequired(data.description, 'Description');

    // Year validation (basic checks, no overlap validation per requirements)
    if (!data.start_year) {
      validationErrors.start_year = ['Start year is required'];
    } else if (data.start_year > new Date().getFullYear()) {
      validationErrors.start_year = ['Start year cannot be in the future'];
    }

    // Month validation
    if (!data.start_month || data.start_month < 1 || data.start_month > 12) {
      validationErrors.start_month = ['Start month is required'];
    }

    // End date validation (optional, but if provided should be valid)
    if (data.end_year && data.end_year > new Date().getFullYear()) {
      validationErrors.end_year = ['End year cannot be in the future'];
    }

    if (data.end_month && (data.end_month < 1 || data.end_month > 12)) {
      validationErrors.end_month = ['End month must be between 1 and 12'];
    }

    // Remove empty error arrays
    Object.keys(validationErrors).forEach(key => {
      if (validationErrors[key].length === 0) {
        delete validationErrors[key];
      }
    });

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  };

  // Handle form submission for experience
  const onSubmitExperience = async (data: ExperienceEntry) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate the form data
      const validationResult = validateExperienceEntry(data);
      
      if (!validationResult.isValid) {
        setIsSubmitting(false);
        return;
      }

      // Clean up the data
      const cleanData: ExperienceEntry = {
        organization_name: data.organization_name.trim(),
        position: data.position.trim(),
        start_month: Number(data.start_month),
        start_year: Number(data.start_year),
        end_month: data.end_month ? Number(data.end_month) : undefined,
        end_year: data.end_year ? Number(data.end_year) : undefined,
        responsibilities: data.responsibilities.trim(),
        achievements: data.achievements?.trim() || undefined
      };

      if (editingId.experience !== null) {
        // Update existing entry
        await onUpdateExperience(editingId.experience, cleanData);
        setEditingId(prev => ({ ...prev, experience: null }));
      } else {
        // Add new entry
        await onAddExperience(cleanData);
      }

      // Reset form and close modal
      experienceForm.reset();
      setShowModal(prev => ({ ...prev, experience: false }));
    } catch (error: any) {
      setSubmitError(error.message || 'Gagal menyimpan data pengalaman. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form submission for project
  const onSubmitProject = async (data: ProjectEntry) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate the form data
      const validationResult = validateProjectEntry(data);
      
      if (!validationResult.isValid) {
        setIsSubmitting(false);
        return;
      }

      // Clean up the data
      const cleanData: ProjectEntry = {
        project_name: data.project_name.trim(),
        role: data.role.trim(),
        start_month: Number(data.start_month),
        start_year: Number(data.start_year),
        end_month: data.end_month ? Number(data.end_month) : undefined,
        end_year: data.end_year ? Number(data.end_year) : undefined,
        description: data.description.trim(),
        results: data.results?.trim() || undefined
      };

      if (editingId.projects !== null) {
        // Update existing entry
        await onUpdateProject(editingId.projects, cleanData);
        setEditingId(prev => ({ ...prev, projects: null }));
      } else {
        // Add new entry
        await onAddProject(cleanData);
      }

      // Reset form and close modal
      projectForm.reset();
      setShowModal(prev => ({ ...prev, projects: false }));
    } catch (error: any) {
      setSubmitError(error.message || 'Gagal menyimpan data proyek. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit button click for experience
  const handleEditExperience = (index: number) => {
    const experience = experiences[index];
    if (!experience.id) {
      setSubmitError('Cannot edit experience entry without ID');
      return;
    }
    setEditingId(prev => ({ ...prev, experience: experience.id! }));
    setShowModal(prev => ({ ...prev, experience: true }));
    
    // Populate form with existing data
    experienceForm.setValue('organization_name', experience.organization_name);
    experienceForm.setValue('position', experience.position);
    experienceForm.setValue('start_month', experience.start_month);
    experienceForm.setValue('start_year', experience.start_year);
    experienceForm.setValue('end_month', experience.end_month || undefined);
    experienceForm.setValue('end_year', experience.end_year || undefined);
    experienceForm.setValue('responsibilities', experience.responsibilities);
    experienceForm.setValue('achievements', experience.achievements || '');
  };

  // Handle edit button click for project
  const handleEditProject = (index: number) => {
    const project = projects[index];
    if (!project.id) {
      setSubmitError('Cannot edit project entry without ID');
      return;
    }
    setEditingId(prev => ({ ...prev, projects: project.id! }));
    setShowModal(prev => ({ ...prev, projects: true }));
    
    // Populate form with existing data
    projectForm.setValue('project_name', project.project_name);
    projectForm.setValue('role', project.role);
    projectForm.setValue('start_month', project.start_month);
    projectForm.setValue('start_year', project.start_year);
    projectForm.setValue('end_month', project.end_month || undefined);
    projectForm.setValue('end_year', project.end_year || undefined);
    projectForm.setValue('description', project.description);
    projectForm.setValue('results', project.results || '');
  };

  // Handle delete with confirmation for experience
  const handleDeleteExperience = async (index: number) => {
    const experience = experiences[index];
    if (!experience.id) {
      setSubmitError('Cannot delete experience entry without ID');
      return;
    }
    
    if (globalThis.confirm('Apakah Anda yakin ingin menghapus data pengalaman ini?')) {
      try {
        await onDeleteExperience(experience.id);
      } catch (error: any) {
        setSubmitError(error.message || 'Gagal menghapus data pengalaman. Silakan coba lagi.');
      }
    }
  };

  // Handle delete with confirmation for project
  const handleDeleteProject = async (index: number) => {
    const project = projects[index];
    if (!project.id) {
      setSubmitError('Cannot delete project entry without ID');
      return;
    }
    
    if (globalThis.confirm('Apakah Anda yakin ingin menghapus data proyek ini?')) {
      try {
        await onDeleteProject(project.id);
      } catch (error: any) {
        setSubmitError(error.message || 'Gagal menghapus data proyek. Silakan coba lagi.');
      }
    }
  };

  // Handle cancel add/edit
  const handleCancel = (section: 'experience' | 'projects') => {
    setShowModal(prev => ({ ...prev, [section]: false }));
    setEditingId(prev => ({ ...prev, [section]: null }));
    setSubmitError(null);
    experienceForm.reset();
    projectForm.reset();
  };

  // Handle add new button
  const handleAddNew = (section: 'experience' | 'projects') => {
    setShowModal(prev => ({ ...prev, [section]: true }));
    setEditingId(prev => ({ ...prev, [section]: null }));
    setSubmitError(null);
    experienceForm.reset();
    projectForm.reset();
  };

  // Format date for display
  const formatDate = (month?: number, year?: number) => {
    if (!year) return 'Sekarang';
    if (!month) return year.toString();
    const monthName = monthOptions.find(m => m.value === month)?.label || '';
    return `${monthName} ${year}`;
  };

  return (
    <div className="cv-experience-manager">
      <div className="flex flex-wrap">
        <div className="w-full">
          {submitError && (
            <div
              className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
              role="alert"
            >
              {submitError}
            </div>
          )}

          {/* Experience Section */}
          <div className="experience-section mb-5">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">Pengalaman Kerja</h4>
            </div>

            {/* Experience Entries List */}
            {experiences.length > 0 && (
              <div className="experience-entries mb-4">
                {experiences.map((experience, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow border mb-3"
                  >
                    <div className="p-4">
                      <div className="flex flex-wrap flex-col">
                        <div className="md:w-8/12">
                          <h5 className="text-lg font-semibold mb-2">
                            {experience.position}
                          </h5>
                          <p className="text-gray-700 mb-1">
                            <strong>Organisasi:</strong>{" "}
                            {experience.organization_name}
                          </p>
                          <p className="text-gray-700 mb-1">
                            <strong>Periode:</strong>{" "}
                            {formatDate(
                              experience.start_month,
                              experience.start_year
                            )}{" "}
                            -{" "}
                            {formatDate(
                              experience.end_month,
                              experience.end_year
                            )}
                          </p>
                          <p className="text-gray-700 mb-1">
                            <strong>Tanggung Jawab:</strong>{" "}
                            {experience.responsibilities}
                          </p>
                          {experience.achievements && (
                            <p className="text-gray-700 mb-0">
                              <strong>Pencapaian:</strong>{" "}
                              {experience.achievements}
                            </p>
                          )}
                        </div>
                        <div className="md:w-4/12 flex items-center justify-end gap-2">
                          <a
                            onClick={() => handleEditExperience(index)}
                            className="cursor-pointer"
                          >
                            <Edit className="text-primary" size={"1rem"} />
                          </a>
                          <a
                            className="cursor-pointer"
                            onClick={() => handleDeleteExperience(index)}
                          >
                            <Trash className="text-red-600" size={"1rem"} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {experiences.length > 0 && (
              <div className="flex justify-end items-center">
                <Button
                  primary
                  onClick={() => handleAddNew("experience")}
                  className="px-3 flex items-center gap-2 gradient-primary"
                >
                  <Plus /> Tambah
                </Button>
              </div>
            )}

            {/* Empty State for Experience */}
            {experiences.length === 0 && (
              <div className="text-center py-5">
                <p className="text-gray-500 mb-3">
                  Belum ada data pengalaman kerja yang ditambahkan.
                </p>
                <Button
                  primary
                  onClick={() => handleAddNew("experience")}
                  className="px-4 flex items-center gap-2 gradient-primary mx-auto"
                >
                  <Plus /> Tambah Pengalaman Kerja
                </Button>
              </div>
            )}
          </div>
          <hr className="my-3" />
          {/* Projects Section */}
          <div className="projects-section mb-5">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">Proyek</h4>
            </div>

            {/* Project Entries List */}
            {projects.length > 0 && (
              <div className="project-entries mb-4">
                {projects.map((project, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow border mb-3"
                  >
                    <div className="p-4">
                      <div className="flex flex-wrap">
                        <div className="md:w-8/12">
                          <h5 className="text-lg font-semibold mb-2 mb-2">
                            {project.project_name}
                          </h5>
                          <p className="text-gray-700 mb-1">
                            <strong>Peran:</strong> {project.role}
                          </p>
                          <p className="text-gray-700 mb-1">
                            <strong>Periode:</strong>{" "}
                            {formatDate(
                              project.start_month,
                              project.start_year
                            )}{" "}
                            - {formatDate(project.end_month, project.end_year)}
                          </p>
                          <p className="text-gray-700 mb-1">
                            <strong>Deskripsi:</strong> {project.description}
                          </p>
                          {project.results && (
                            <p className="text-gray-700 mb-0">
                              <strong>Hasil:</strong> {project.results}
                            </p>
                          )}
                        </div>
                        <div className="md:w-4/12 flex items-center justify-end gap-2">
                          <a
                            onClick={() => handleEditProject(index)}
                            className="cursor-pointer"
                          >
                            <Edit className="text-primary" size={"1rem"} />
                          </a>
                          <a
                            className="cursor-pointer"
                            onClick={() => handleDeleteProject(index)}
                          >
                            <Trash className="text-red-600" size={"1rem"} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {projects.length > 0 && (
              <div className="flex justify-end items-center">
                <Button
                  primary
                  onClick={() => handleAddNew("projects")}
                  className="px-3 flex items-center gap-2 gradient-primary"
                >
                  <Plus /> Tambah
                </Button>
              </div>
            )}
            {/* Empty State for Projects */}
            {projects.length === 0 && (
              <div className="text-center py-5 flex flex-col items-center justify-center">
                <p className="text-gray-500 mb-3">
                  Belum ada data proyek yang ditambahkan.
                </p>
                <Button
                  primary
                  onClick={() => handleAddNew("projects")}
                  className="px-4 flex items-center gap-2 mx-auto gradient-primary"
                >
                  <Plus /> Tambah Proyek
                </Button>
              </div>
            )}
          </div>

          {/* Experience Modal */}
          <Modal
            show={showModal.experience}
            onHide={() => handleCancel("experience")}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editingId.experience !== null
                  ? "Ubah Data Pengalaman"
                  : "Tambah Data Pengalaman Baru"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError && (
                <div
                  className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
                  role="alert"
                >
                  {submitError}
                </div>
              )}

              <form
                onSubmit={experienceForm.handleSubmit(onSubmitExperience)}
                noValidate
              >
                <div className="flex flex-wrap">
                  {/* Organization Name */}
                  <div className="w-full md:w-6/12 mb-3">
                    <label htmlFor="organization_name" className="form-label">
                      Nama Organisasi <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="Masukkan nama organisasi"
                      name="organization_name"
                      register={experienceForm.register as any}
                      errors={experienceForm.formState.errors as any}
                      rules={{
                        required: "Nama organisasi wajib diisi",
                        minLength: {
                          value: 2,
                          message: "Nama organisasi minimal 2 karakter",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Position */}
                  <div className="w-full md:w-6/12 mb-3">
                    <label htmlFor="position" className="form-label">
                      Posisi <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="Masukkan posisi/jabatan Anda"
                      name="position"
                      register={experienceForm.register as any}
                      errors={experienceForm.formState.errors as any}
                      rules={{
                        required: "Posisi wajib diisi",
                        minLength: {
                          value: 2,
                          message: "Posisi minimal 2 karakter",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap">
                  {/* Start Month */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="start_month" className="form-label">
                      Bulan Mulai <span className="text-red-600">*</span>
                    </label>
                    <select
                      className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 border border-slate-200 rounded-xl pl-2 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md cursor-pointer"
                      {...experienceForm.register("start_month", {
                        required: "Bulan mulai wajib diisi",
                        valueAsNumber: true,
                      })}
                      disabled={isSubmitting}
                    >
                      {monthOptions.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    {experienceForm.formState.errors.start_month && (
                      <p className="text-red-600 italic mb-2">
                        {experienceForm.formState.errors.start_month.message}
                      </p>
                    )}
                  </div>

                  {/* Start Year */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="start_year" className="form-label">
                      Tahun Mulai <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="2020"
                      type="number"
                      name="start_year"
                      register={experienceForm.register as any}
                      errors={experienceForm.formState.errors as any}
                      rules={{
                        required: "Tahun mulai wajib diisi",
                        min: {
                          value: 1950,
                          message: "Tahun mulai harus setelah 1950",
                        },
                        max: {
                          value: new Date().getFullYear(),
                          message: "Tahun mulai tidak boleh di masa depan",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* End Month */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="end_month" className="form-label">
                      Bulan Selesai{" "}
                      <span className="text-gray-500">(Opsional)</span>
                    </label>
                    <select
                      className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 border border-slate-200 rounded-xl pl-2 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md cursor-pointer"
                      {...experienceForm.register("end_month", {
                        valueAsNumber: true,
                      })}
                      disabled={isSubmitting}
                    >
                      <option value="">Sekarang</option>
                      {monthOptions.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    {experienceForm.formState.errors.end_month && (
                      <p className="text-red-600 italic mb-2">
                        {experienceForm.formState.errors.end_month.message}
                      </p>
                    )}
                  </div>

                  {/* End Year */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="end_year" className="form-label">
                      Tahun Selesai{" "}
                      <span className="text-gray-500">(Opsional)</span>
                    </label>
                    <Input
                      placeholder="2024"
                      type="number"
                      name="end_year"
                      register={experienceForm.register as any}
                      errors={experienceForm.formState.errors as any}
                      rules={{
                        min: {
                          value: 1950,
                          message: "Tahun selesai harus setelah 1950",
                        },
                        max: {
                          value: new Date().getFullYear(),
                          message: "Tahun selesai tidak boleh di masa depan",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Responsibilities */}
                <div className="w-full mb-3">
                  <label htmlFor="responsibilities" className="form-label">
                    Tanggung Jawab <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Jelaskan tanggung jawab dan tugas utama Anda"
                    rows={4}
                    {...experienceForm.register("responsibilities", {
                      required: "Tanggung jawab wajib diisi",
                    })}
                    disabled={isSubmitting}
                  />
                  {experienceForm.formState.errors.responsibilities && (
                    <p className="text-red-600 italic mb-2">
                      {experienceForm.formState.errors.responsibilities.message}
                    </p>
                  )}
                </div>

                {/* Achievements */}
                <div className="mb-4">
                  <label htmlFor="achievements" className="form-label">
                    Pencapaian <span className="text-gray-500">(Opsional)</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Jelaskan pencapaian dan prestasi utama Anda"
                    rows={3}
                    {...experienceForm.register("achievements")}
                    disabled={isSubmitting}
                  />
                  {experienceForm.formState.errors.achievements && (
                    <p className="text-red-600 italic mb-2">
                      {experienceForm.formState.errors.achievements.message}
                    </p>
                  )}
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCancel("experience")}
                disabled={isSubmitting}
                className="px-4"
              >
                Batal
              </Button>
              <Button
                type="submit"
                primary
                disabled={isSubmitting}
                className="px-4 flex items-center gap-2 gradient-primary"
                onClick={experienceForm.handleSubmit(onSubmitExperience)}
              >
                <Save /> {isSubmitting ? "Menyimpan..." : "Simpan Data"}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Projects Modal */}
          <Modal
            show={showModal.projects}
            onHide={() => handleCancel("projects")}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editingId.projects !== null
                  ? "Ubah Data Proyek"
                  : "Tambah Data Proyek Baru"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError && (
                <div
                  className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
                  role="alert"
                >
                  {submitError}
                </div>
              )}

              <form
                onSubmit={projectForm.handleSubmit(onSubmitProject)}
                noValidate
              >
                <div className="flex flex-wrap">
                  {/* Project Name */}
                  <div className="w-full md:w-6/12 mb-3">
                    <label htmlFor="project_name" className="form-label">
                      Nama Proyek <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="Masukkan nama proyek"
                      name="project_name"
                      register={projectForm.register as any}
                      errors={projectForm.formState.errors as any}
                      rules={{
                        required: "Nama proyek wajib diisi",
                        minLength: {
                          value: 2,
                          message: "Nama proyek minimal 2 karakter",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Role */}
                  <div className="w-full md:w-6/12 mb-3">
                    <label htmlFor="role" className="form-label">
                      Peran <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="Masukkan peran Anda dalam proyek"
                      name="role"
                      register={projectForm.register as any}
                      errors={projectForm.formState.errors as any}
                      rules={{
                        required: "Peran wajib diisi",
                        minLength: {
                          value: 2,
                          message: "Peran minimal 2 karakter",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap">
                  {/* Start Month */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="start_month" className="form-label">
                      Bulan Mulai <span className="text-red-600">*</span>
                    </label>
                    <select
                      className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 border border-slate-200 rounded-xl pl-2 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md cursor-pointer"
                      {...projectForm.register("start_month", {
                        required: "Bulan mulai wajib diisi",
                        valueAsNumber: true,
                      })}
                      disabled={isSubmitting}
                    >
                      {monthOptions.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    {projectForm.formState.errors.start_month && (
                      <p className="text-red-600 italic mb-2">
                        {projectForm.formState.errors.start_month.message}
                      </p>
                    )}
                  </div>

                  {/* Start Year */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="start_year" className="form-label">
                      Tahun Mulai <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="2020"
                      type="number"
                      name="start_year"
                      register={projectForm.register as any}
                      errors={projectForm.formState.errors as any}
                      rules={{
                        required: "Tahun mulai wajib diisi",
                        min: {
                          value: 1950,
                          message: "Tahun mulai harus setelah 1950",
                        },
                        max: {
                          value: new Date().getFullYear(),
                          message: "Tahun mulai tidak boleh di masa depan",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* End Month */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="end_month" className="form-label">
                      Bulan Selesai{" "}
                      <span className="text-gray-500">(Opsional)</span>
                    </label>
                    <select
                      className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 border border-slate-200 rounded-xl pl-2 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md cursor-pointer"
                      {...projectForm.register("end_month", {
                        valueAsNumber: true,
                      })}
                      disabled={isSubmitting}
                    >
                      <option value="">Sekarang</option>
                      {monthOptions.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    {projectForm.formState.errors.end_month && (
                      <p className="text-red-600 italic mb-2">
                        {projectForm.formState.errors.end_month.message}
                      </p>
                    )}
                  </div>

                  {/* End Year */}
                  <div className="w-full md:w-3/12 mb-3">
                    <label htmlFor="end_year" className="form-label">
                      Tahun Selesai{" "}
                      <span className="text-gray-500">(Opsional)</span>
                    </label>
                    <Input
                      placeholder="2024"
                      type="number"
                      name="end_year"
                      register={projectForm.register as any}
                      errors={projectForm.formState.errors as any}
                      rules={{
                        min: {
                          value: 1950,
                          message: "Tahun selesai harus setelah 1950",
                        },
                        max: {
                          value: new Date().getFullYear(),
                          message: "Tahun selesai tidak boleh di masa depan",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="w-full mb-3">
                  <label htmlFor="description" className="form-label">
                    Deskripsi <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Jelaskan proyek dan kontribusi Anda"
                    rows={4}
                    {...projectForm.register("description", {
                      required: "Deskripsi wajib diisi",
                    })}
                    disabled={isSubmitting}
                  />
                  {projectForm.formState.errors.description && (
                    <p className="text-red-600 italic mb-2">
                      {projectForm.formState.errors.description.message}
                    </p>
                  )}
                </div>

                {/* Results */}
                <div className="w-full mb-4">
                  <label htmlFor="results" className="form-label">
                    Hasil <span className="text-gray-500">(Opsional)</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Jelaskan hasil dan dampak dari proyek"
                    rows={3}
                    {...projectForm.register("results")}
                    disabled={isSubmitting}
                  />
                  {projectForm.formState.errors.results && (
                    <p className="text-red-600 italic mb-2">
                      {projectForm.formState.errors.results.message}
                    </p>
                  )}
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-1">
              <Button
                type="button"
                outline
                onClick={() => handleCancel("projects")}
                disabled={isSubmitting}
                className="px-4"
              >
                Batal
              </Button>
              <Button
                type="submit"
                primary
                disabled={isSubmitting}
                className="px-4 flex items-center gap-2 gradient-primary"
                onClick={projectForm.handleSubmit(onSubmitProject)}
              >
                <Save /> {isSubmitting ? "Menyimpan..." : "Simpan Data"}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default CVExperienceManager;